import {
  Box,
  Center,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { SkillSetContractCategories } from '@duke-hq/shared-constants/enrolments';
import { Plural, Trans } from '@lingui/react/macro';
import { ArrowRightFromLine } from 'lucide-react';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { trpcTenant } from '../trpcTenant';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { EnrolmentDetails } from '../EnrolmentDetails';
import { CenteredSpinner } from '../../components/Loading';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { TeamBulkEnrolmentsFormModal } from '@duke-hq/app-provider-web/tenant/Team/TeamBulkEnrolments/TeamBulkEnrolmentsFormModal';
import { TeamEnrolmentsTable } from '@duke-hq/app-provider-web/tenant/Team/TeamEnrolmentsTable';
import ProviderFilterBar from '../../components/FilterBar/ProviderFilterBar';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { emptyPaginationResponse, SortOrder, parseSortOrder } from '@duke-hq/lib/pagination';

export function TeamEnrolments(): React.ReactElement {
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const [querySearch, setQuerySearch] = useState('');
  const navigate = useNavigate();
  const { enrolmentId } = useParams<{ enrolmentId: string }>();
  const { pageSize, currentPage, setPage } = usePagination();
  const [searchParams] = useSearchParams();
  const { filters, filtersLoaded } = useFiltersContext();

  const allFilters = useMemo(() => {
    return {
      sort: parseSortOrder(searchParams.get('sort'), SortOrder.DESC),
      orderBy: searchParams.get('orderBy') ?? 'created_at',
      status: searchParams.get('status') ?? undefined,
      health: filters.health?.join(','),
      siteLocationIds: filters.sites,
      siteRegionIds: filters.regions,
      consultantIds: filters.consultants?.join(','),
      courseIds: filters.courses?.join(','),
      dueDateStart: filters.dueDateStart,
      dueDateEnd: filters.dueDateEnd,
      percent: filters.percent,
      contractIds: filters.contractIds?.join(','),
      category: SkillSetContractCategories.INTERNAL_ENROLMENTS,
    };
  }, [filters, searchParams]);

  const { data: paginatedEnrolments, isFetching } = trpcTenant.enrolments.all.useQuery(
    {
      search: querySearch.trim(),
      filters: allFilters,
      pagination: {
        page: Number(currentPage),
        limit: pageSize,
      },
    },
    {
      initialData: emptyPaginationResponse(),
      enabled: filtersLoaded,
    },
  );

  const { isOpen } = useDisclosure({
    isOpen: !!enrolmentId,
  });

  const onCloseHandler = useCallback(() => {
    navigate(`/team/enrolments?${searchParams.toString()}`);
  }, [navigate, searchParams]);

  return (
    <>
      {enrolmentId ? (
        <Drawer isOpen={isOpen} onClose={onCloseHandler} size="xl">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton onClick={onCloseHandler} />
            <Suspense fallback={<CenteredSpinner />}>
              <EnrolmentDetails />
            </Suspense>
          </DrawerContent>
        </Drawer>
      ) : null}

      <TableViewLayout
        pageHeader={[
          !isExpanded && (
            <IconButton
              key="expand"
              aria-label="expand secondary sidebar"
              icon={<ArrowRightFromLine />}
              variant="tertiary"
              onClick={toggleExpandSubSidebar}
              size="md"
            />
          ),
          <PageHeading key="page-heading">
            <Trans>Internal Enrolments</Trans>
          </PageHeading>,
        ]}
        pageActions={[
          <Box key={'teamInternalEnrolmentFormModal'}>
            <TeamBulkEnrolmentsFormModal />
          </Box>,
        ]}
        filters={
          <>
            <SearchInput maxW="search-input" onCommit={setQuerySearch} />
            <ProviderFilterBar />
          </>
        }
        tableActionsLeft={
          <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
            <Plural
              value={paginatedEnrolments.pagination.totalCount || 0}
              zero="0 enrolments"
              one="1 enrolment"
              other="# enrolments"
            />
          </Text>
        }
        table={
          isFetching ? (
            <Center p={8}>
              <Spinner size="xl" transitionDelay={'2900ms'} />
            </Center>
          ) : paginatedEnrolments.items && paginatedEnrolments.items.length > 0 ? (
            <TeamEnrolmentsTable enrolments={paginatedEnrolments.items} />
          ) : (
            <Center p={8}>
              <Text textAlign="center">
                <Trans>No enrolments found</Trans>
              </Text>
            </Center>
          )
        }
        pagination={
          paginatedEnrolments.items.length > 0 ? (
            <Pagination pagination={paginatedEnrolments.pagination} onPageChange={setPage} />
          ) : null
        }
      />
    </>
  );
}
